import {Injectable} from '@angular/core';
import {NavigationStart, Router} from '@angular/router';
import {Observable, Subject} from 'rxjs';
import {HttpErrorResponse} from '@angular/common/http';
import {ServerError} from '../interface/serverError';

/**
 * A call-to-action rendered in an alert/modal footer. The kind of control is selected by the discriminating
 * {@link AlertActionBase.type}, so the alert component stays generic: it knows how to render each action kind rather
 * than assuming every action is a link. Callers own the styling via {@link AlertActionBase.cssClass} (required, so an
 * action is never rendered unstyled now that the component carries no default class), and further kinds can be added to
 * the union without changing existing call-sites.
 */
export interface AlertActionBase
{
    label: string;
    cssClass: string;
}

/**
 * An action that navigates to {@link url}, rendered as an anchor.
 */
export interface LinkAction extends AlertActionBase
{
    type: 'link';
    url: string;
    /** When true the link opens in a new tab (target="_blank" with rel="noopener noreferrer"); otherwise the same tab. */
    openInNewTab?: boolean;
}

/**
 * The kinds of action an alert may carry. Currently only {@link LinkAction}; kept as a discriminated union so a new
 * kind (e.g. a callback button) can be added here and handled in the alert template without touching existing callers.
 */
export type AlertAction = LinkAction;

@Injectable({
    providedIn: 'root'
})
export class AlertService
{

    private subject = new Subject<any>();
    private keepAfterNavigationChange = false;

    constructor(private router: Router)
    {
        // clear alert message on route change
        router.events.subscribe(event =>
        {
            if (event instanceof NavigationStart)
            {
                if (this.keepAfterNavigationChange)
                {
                    // only keep for a single location change
                    this.keepAfterNavigationChange = false;
                }
                else
                {
                    // clear alert
                    this.subject.next(null);
                }
            }
        });
    }

    info(message: string, keepAfterNavigationChange = false)
    {
        this.keepAfterNavigationChange = keepAfterNavigationChange;
        this.subject.next({type: 'INFO', text: message});
        window.scrollTo(0, 0);
    }

    lockout(message: string, remainingSeconds: number)
    {
        this.keepAfterNavigationChange = false;
        this.subject.next({type: 'LOCKOUT', text: message, remainingSeconds: remainingSeconds});
        window.scrollTo(0, 0);
    }

    success(
        message: string, keepAfterNavigationChange = false, dataModal?: string,
        action?: AlertAction)
    {
        this.keepAfterNavigationChange = keepAfterNavigationChange;
        this.subject.next({type: 'SUCCESS', text: message, dataModal: dataModal, action: action});
        window.scrollTo(0, 0);
    }

    error(error: any, keepAfterNavigationChange = false)
    {
        this.keepAfterNavigationChange = keepAfterNavigationChange;

        if (error instanceof HttpErrorResponse)
        {
            this.subject.next({type: 'ERROR', text: this.handleError(error)});
        }

        if (typeof error === 'string')
        {
            this.subject.next({type: 'ERROR', text: error});
        }
        window.scrollTo(0, 0);
    }

    /**
     * Handles an HTTPErrorResponse by distinguishing between client and server error responses,
     * and pulling out the correct error text to display in an alert model, based on known types.
     */
    handleError(error: HttpErrorResponse): string
    {
        // The server returned a json error response with a known type
        if (error.error && (<ServerError> error.error).errorMessage)
        {
            return (<ServerError> error.error).errorMessage;
        }
        else if (error instanceof ErrorEvent)
        {
            return (<ErrorEvent> error).message;
        }
        else if (error.status >= 500 && error.status < 600)
        {
            return 'Server is currently unavailable: ' + error.message;
        }
        // use the text from the response body as the error text
        return (error.error) ? error.error : error.message;
    }

    getMessage(): Observable<any>
    {
        return this.subject.asObservable();
    }
}
